import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Plus, Trash2, Upload } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import StickyActions from '../../../components/StickyActions';
import SelectorClienteProveedor from '../../../components/Tesoreria/SelectorClienteProveedor';
import SelectorComprobantes from '../../../components/Tesoreria/SelectorComprobantes';
import SelectorMedioPago from '../../../components/Tesoreria/SelectorMedioPago';
import SelectorCuenta from '../../../components/Tesoreria/SelectorCuenta';
import { usePago, usePagoMutations } from '../../../graphql/tesoreria';

export default function EditarPago() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    proveedorId: '',
    proveedor: null,
    moneda: 'ARS',
    medioPagoId: '',
    medioPago: null,
    cuentaBancariaId: '',
    observaciones: '',
    imputaciones: [],
    adjuntos: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSelectorProveedor, setShowSelectorProveedor] = useState(false);
  const [showSelectorComprobantes, setShowSelectorComprobantes] = useState(false);
  const [adjuntoUrl, setAdjuntoUrl] = useState('');

  const [result] = usePago(id);
  const { createPago, updatePago } = usePagoMutations();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  const calcularTotalImputado = () => {
    return formData.imputaciones.reduce((sum, imp) => sum + (imp.monto || 0), 0);
  };

  const calcularEstado = () => {
    const totalImputado = calcularTotalImputado();
    if (totalImputado === 0) return 'PENDIENTE';
    
    // Calcular si hay saldo pendiente en algún comprobante
    const haySaldoPendiente = formData.imputaciones.some(
      (imp) => imp.saldoAnterior > imp.monto
    );
    
    return haySaldoPendiente ? 'PARCIAL' : 'CERRADO';
  };

  const handleAddAdjunto = () => {
    if (adjuntoUrl.trim()) {
      setFormData({
        ...formData,
        adjuntos: [
          ...formData.adjuntos,
          { url: adjuntoUrl, nombre: adjuntoUrl.split('/').pop() || 'Adjunto' },
        ],
      });
      setAdjuntoUrl('');
    }
  };

  const handleRemoveAdjunto = (index) => {
    setFormData({
      ...formData,
      adjuntos: formData.adjuntos.filter((_, i) => i !== index),
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fecha) newErrors.fecha = 'La fecha es obligatoria';
    if (!formData.proveedorId) newErrors.proveedorId = 'Debe seleccionar un proveedor';
    if (!formData.medioPagoId) newErrors.medioPagoId = 'Debe seleccionar un medio de pago';
    if (formData.imputaciones.length === 0) {
      newErrors.imputaciones = 'Debe imputar al menos un comprobante';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const input = {
        ...formData,
        importe: calcularTotalImputado(),
        estado: calcularEstado(),
      };

      if (isEditing) {
        await updatePago({ id, input });
        alert('Pago actualizado correctamente');
      } else {
        await createPago({ input });
        alert('Pago creado correctamente');
      }
      navigate('/Tesoreria/Pagos');
    } catch (error) {
      alert(`Error al ${isEditing ? 'actualizar' : 'crear'} el pago`);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: formData.moneda || 'ARS',
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))] pb-24">
      <PageHeader
        title={isEditing ? 'Editar Pago' : 'Nuevo Pago a Proveedor'}
        subtitle={isEditing ? 'Modificar información del pago' : 'Registrar un nuevo pago'}
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        {/* Cabecera */}
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <h3 className="text-lg font-semibold text-[rgb(var(--fg))] mb-4">Datos del Pago</h3>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                Fecha <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                  errors.fecha ? 'border-red-500' : 'border-[rgb(var(--border))]'
                }`}
              />
              {errors.fecha && <p className="mt-1 text-sm text-red-500">{errors.fecha}</p>}
            </div>

            <div className="col-span-12 md:col-span-8">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                Proveedor <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowSelectorProveedor(true)}
                className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-left focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                  errors.proveedorId ? 'border-red-500' : 'border-[rgb(var(--border))]'
                }`}
              >
                {formData.proveedor ? (
                  <span className="text-[rgb(var(--fg))]">{formData.proveedor.nombre}</span>
                ) : (
                  <span className="text-[rgb(var(--muted))]">Seleccionar proveedor</span>
                )}
              </button>
              {errors.proveedorId && <p className="mt-1 text-sm text-red-500">{errors.proveedorId}</p>}
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                Moneda <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.moneda}
                onChange={(e) => setFormData({ ...formData, moneda: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div className="col-span-12 md:col-span-4">
              <SelectorMedioPago
                value={formData.medioPagoId}
                onChange={(medio) => setFormData({ ...formData, medioPagoId: medio?.id || '', medioPago: medio })}
                label="Medio de Pago"
                required
                error={errors.medioPagoId}
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <SelectorCuenta
                value={formData.cuentaBancariaId}
                onChange={(cuenta) => setFormData({ ...formData, cuentaBancariaId: cuenta?.id || '' })}
                label="Cuenta Bancaria"
                placeholder="Seleccionar cuenta"
              />
            </div>

            <div className="col-span-12">
              <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Observaciones</label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                rows={2}
                className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] resize-none"
                placeholder="Notas adicionales..."
              />
            </div>
          </div>
        </div>

        {/* Imputaciones */}
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Imputación de Comprobantes</h3>
            <button
              onClick={() => setShowSelectorComprobantes(true)}
              disabled={!formData.proveedorId}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar Comprobantes
            </button>
          </div>

          {errors.imputaciones && (
            <p className="mb-4 text-sm text-red-500">{errors.imputaciones}</p>
          )}

          {formData.imputaciones.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Comprobante</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Saldo</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Imputado</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--border))]">
                  {formData.imputaciones.map((imp, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-[rgb(var(--fg))]">{imp.comprobanteNumero}</td>
                      <td className="px-4 py-3 text-sm text-right text-[rgb(var(--muted))]">{formatCurrency(imp.saldoAnterior || 0)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-[rgb(var(--fg))]">{formatCurrency(imp.monto || 0)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            setFormData({
                              ...formData,
                              imputaciones: formData.imputaciones.filter((_, i) => i !== index),
                            });
                          }}
                          className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-[rgba(var(--brand),0.05)] font-semibold">
                    <td colSpan={2} className="px-4 py-3 text-right text-[rgb(var(--fg))]">Total:</td>
                    <td className="px-4 py-3 text-right text-[rgb(var(--fg))]">{formatCurrency(calcularTotalImputado())}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-[rgb(var(--muted))] py-8">No hay comprobantes imputados</p>
          )}
        </div>

        {/* Adjuntos */}
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <h3 className="text-lg font-semibold text-[rgb(var(--fg))] mb-4">Adjuntos</h3>
          <div className="flex gap-2 mb-4">
            <input
              type="url"
              value={adjuntoUrl}
              onChange={(e) => setAdjuntoUrl(e.target.value)}
              placeholder="URL del adjunto"
              className="flex-1 px-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
            <button
              onClick={handleAddAdjunto}
              className="px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {formData.adjuntos.length > 0 && (
            <div className="space-y-2">
              {formData.adjuntos.map((adj, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-[rgba(var(--subpanel))] border border-[rgb(var(--border))]">
                  <a href={adj.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[rgb(var(--brand))] hover:underline">{adj.nombre}</a>
                  <button onClick={() => handleRemoveAdjunto(index)} className="p-1 rounded hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <StickyActions
        actions={[
          <button key="guardar" onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>,
          <button key="cancelar" onClick={() => navigate('/Tesoreria/Pagos')} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors">
            <X className="w-4 h-4" />
            Cancelar
          </button>,
        ]}
      />

      <SelectorClienteProveedor
        isOpen={showSelectorProveedor}
        onClose={() => setShowSelectorProveedor(false)}
        onSelect={(proveedor) => setFormData({ ...formData, proveedorId: proveedor.id, proveedor })}
        mode="proveedor"
        selectedId={formData.proveedorId}
      />

      <SelectorComprobantes
        isOpen={showSelectorComprobantes}
        onClose={() => setShowSelectorComprobantes(false)}
        onConfirm={(imputaciones) => setFormData({ ...formData, imputaciones })}
        entidadId={formData.proveedorId}
        mode="proveedor"
        selected={formData.imputaciones}
      />
    </div>
  );
}
